<script lang="ts" setup>
export interface ModalProps {
    isOpen: boolean
    showCloseBtn?: boolean
    labelledBy?: string
    size?: 'fullwidth' | 'normal' | 'big' | 'xxl' | 'fullscreen'
}

export interface ModalEmits {
    // eslint-disable-next-line no-unused-vars
    (e: 'close'): void
}

const props = withDefaults(defineProps<ModalProps>(), {
    isOpen: false,
    showCloseBtn: true,
    size: 'normal',
    labelledBy: undefined,
})
const scrollLock = useState('scrollLock')
const emit = defineEmits<ModalEmits>()
const modalRef = ref<HTMLElement>()
const overflow = ref<string>('overflow-hidden')
const closeButton = ref<HTMLButtonElement>()
const showContent = ref<boolean>(false)
const modalContent = ref<HTMLDivElement>()

const handleClickOnBackground = (e: Event) => {
    if (e.target !== modalRef.value) return
    e.preventDefault()
    handleClose()
}

const handleClose = (): void => {
    emit('close')
}

// Focus trap for WCAG 2.4.3 compliance
const focusTrap = useFocusTrap(modalRef, {
    initialFocus: closeButton,
    returnFocusOnDeactivate: true,
    onEscape: () => {
        if (props.showCloseBtn) {
            handleClose()
        }
    },
})

// Watch for isOpen changes to activate/deactivate focus trap
watch(() => props.isOpen, (isOpen) => {
    if (isOpen) {
        document.body.classList.add(overflow.value)
        scrollLock.value = true
        showContent.value = true
        // Activate focus trap after content is shown
        nextTick(() => {
            focusTrap.activate()
        })
    } else {
        focusTrap.deactivate()
        document.body.classList.remove(overflow.value)
        scrollLock.value = false
    }
}, { immediate: true })

onUnmounted(() => {
    focusTrap.deactivate()
    document.body.classList.remove(overflow.value)
    scrollLock.value = false
})

provide('modalCloseButton', closeButton)
provide('modalContent', modalContent)
</script>

<template>
  <Teleport to="#modal-wrap">
    <div
      ref="modalRef"
      class="modal d-flex justify-content-center align-items-center position-fixed start-0 top-0 w-100 pb-4 px-4"
      :class="!props.isOpen ? 'd-none' : ''"
      tabindex="-1"
      :aria-labelledby="props.labelledBy || 'modalModalLabel'"
      :aria-hidden="!props.isOpen"
      :aria-modal="props.isOpen"
      :role="props.isOpen ? 'dialog' : ''"
      @click="handleClickOnBackground"
    >
      <div
        v-if="showContent"
        class="modal-content-wrap position-relative flex-grow-1 d-flex align-items-stretch"
        :class="`modal-content-wrap-${props.size}`"
      >
        <!-- Close button -->
        <button
          v-if="props.showCloseBtn"
          ref="closeButton"
          type="button"
          class="btn transition modal-close-btn p-0 position-absolute top-0 end-0 d-flex justify-content-center align-items-center rounded-4 text-decoration-none border-0 z-index-5"
          :aria-label="$t('Zatvoriť')"
          @click.prevent="handleClose"
        >
          <svg
            class="d-block transition"
            width="10"
            height="10"
            aria-hidden="true"
          >
            <use xlink:href="#times" />
          </svg>
        </button>
        <div
          ref="modalContent"
          class="modal-content w-100 rounded-4"
        >
          <slot />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss">
@import './Modal.vars.scss';
</style>
<style scoped lang="scss">
@import './Modal.scss';
</style>
